/**
 * Periods for v2's tables: which columns a model has, and how each is labelled.
 *
 * v2's own copy. It was v1's (types/financial-model), and v2 stands on its own: nothing it
 * renders depends on v1's code. Only the settings fields the period maths reads are named here.
 */

export type Quarter = string;

export type QuarterlyData = Record<string, number>;

/** The fields of a model's settings that decide its columns. */
export interface TablePeriodSettings {
  forecastStartYear?: number;
  numForecastYears?: number;
  periodType?: string;
  historicalYears?: number[];
}

const MONTH_ABBREVS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatQuarter(q: Quarter): string {
  const [prefix, year] = q.split('_');
  if (prefix.startsWith('M')) {
    const monthIdx = parseInt(prefix.slice(1), 10) - 1;
    return `${MONTH_ABBREVS[monthIdx]} ${year}`;
  }
  if (prefix === 'FY') {
    return `FY ${year}`;
  }
  return `${prefix} ${year}`;
}

export interface ForecastContext {
  allQuarters: string[];
  fiscalYears: number[];
  quartersByYear: Record<number, string[]>;
  numQuarters: number;
  periodsPerYear: number;
  periodType: string;
  lastQuarter: string;
  firstQuarter: string;
}

export function getForecastContext(settings: TablePeriodSettings): ForecastContext {
  const startYear = settings.forecastStartYear ?? 2026;
  const numYears = settings.numForecastYears ?? 4;
  const periodType = settings.periodType ?? 'quarterly';
  const fiscalYears: number[] = [];
  const allQuarters: string[] = [];
  const quartersByYear: Record<number, string[]> = {};

  const periodsPerYear = periodType === 'monthly' ? 12 : periodType === 'annual' ? 1 : 4;

  for (let y = startYear; y < startYear + numYears; y++) {
    fiscalYears.push(y);
    let yearPeriods: string[];
    if (periodType === 'monthly') {
      yearPeriods = Array.from({ length: 12 }, (_, i) => `M${String(i + 1).padStart(2, '0')}_${y}`);
    } else if (periodType === 'annual') {
      yearPeriods = [`FY_${y}`];
    } else {
      yearPeriods = [`Q1_${y}`, `Q2_${y}`, `Q3_${y}`, `Q4_${y}`];
    }
    allQuarters.push(...yearPeriods);
    quartersByYear[y] = yearPeriods;
  }

  return {
    allQuarters,
    fiscalYears,
    quartersByYear,
    numQuarters: allQuarters.length,
    periodsPerYear,
    periodType,
    lastQuarter: allQuarters[allQuarters.length - 1],
    firstQuarter: allQuarters[0],
  };
}
